import React, { useState, useEffect } from 'react';
import { fetchTerms as fetchTermsAPI, fetchProfile, archiveTerm, deleteTerm } from '../utils/api';
import { ADMIN_ROLE, ACADEMIC_COORDINATOR_STAFF, STUDENT_SERVICES_STAFF } from '../utils/roles';
import { mapErrorMessage } from '../utils/errorMapping';
import { formatDate, getDateFormat } from '../utils/date';
import CreateTermModal from './CreateTermModal';
import EditTermModal from './EditTermModal';
import './_Buttons.css';
import './Terms.css';

const Terms = () => {
    const [terms, setTerms] = useState([]);
    const [totalTerms, setTotalTerms] = useState(0);
    const [error, setError] = useState('');
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [selectedTerm, setSelectedTerm] = useState(null);
    const [canCreate, setCanCreate] = useState(false);
    const [canUpdate, setCanUpdate] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [dateFormat, setDateFormat] = useState(getDateFormat());

    const fetchTerms = async (showArchived) => {
        try {
            const data = await fetchTermsAPI(showArchived);
            setTerms(data.data || []);
            setTotalTerms(data.total || 0);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    useEffect(() => {
        const checkPermissions = async () => {
            try {
                const profileData = await fetchProfile();
                const userRoles = profileData.data.roles || [];
                const isAdmin = userRoles.some(role => role.id === ADMIN_ROLE);
                const isAcademicCoordinator = userRoles.some(role => role.id === ACADEMIC_COORDINATOR_STAFF);
                const isStudentServices = userRoles.some(role => role.id === STUDENT_SERVICES_STAFF);
                setCanCreate(isAdmin || isAcademicCoordinator);
                setCanUpdate(isAdmin || isAcademicCoordinator || isStudentServices);
                setCanDelete(isAdmin || isAcademicCoordinator);
            } catch (error) {
                console.error("Could not fetch profile for permissions check", error);
            }
        };

        fetchTerms(showArchived);
        checkPermissions();
    }, [showArchived]);

    useEffect(() => {
        const handleDateFormatChange = () => {
            setDateFormat(getDateFormat());
        };

        window.addEventListener('dateFormatChanged', handleDateFormatChange);

        return () => {
            window.removeEventListener('dateFormatChanged', handleDateFormatChange);
        };
    }, []);

    const handleTermCreated = () => {
        fetchTerms(showArchived);
        setCreateModalOpen(false);
    };

    const handleTermUpdated = () => {
        fetchTerms(showArchived);
        setEditModalOpen(false);
    };

    const openEditModal = (term) => {
        setSelectedTerm(term);
        setEditModalOpen(true);
    };

    const handleArchive = async (termId) => {
        try {
            await archiveTerm(termId);
            fetchTerms(showArchived);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handleDelete = async (termId) => {
        if (window.confirm('Are you sure you want to delete this term? All associated classes and groups will also be deleted.')) {
            try {
                await deleteTerm(termId);
                fetchTerms(showArchived);
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        }
    };


    return (
        <div className="invoices-container">
            <div className="header">
                <h1>Terms | {totalTerms}</h1>
                <div className="header-controls">
                    <button onClick={() => setShowArchived(!showArchived)} className="cancel-button text-button">
                        {showArchived ? 'Hide Archived' : 'Show Archived'}
                    </button>
                    {canCreate && (
                        <button onClick={() => setCreateModalOpen(true)} className="add-student-button">+</button>
                    )}
                </div>
            </div>
            {error && <p className="error">{error}</p>}
            {isCreateModalOpen && (
                <CreateTermModal
                    onClose={() => setCreateModalOpen(false)}
                    onTermCreated={handleTermCreated}
                />
            )}
            {isEditModalOpen && (
                <EditTermModal
                    term={selectedTerm}
                    onClose={() => setEditModalOpen(false)}
                    onTermUpdated={handleTermUpdated}
                />
            )}
            <table className="invoices-table terms-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Dates</th>
                        <th>Active</th>
                        {(canUpdate || canDelete) && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {terms.map(term => (
                        <tr key={term.id}>
                            <td>{term.name}</td>
                            <td className="date-cell">
                                <span className="date-value">{formatDate(term.start_date)}</span>
                                <span> - </span>
                                <span className="date-value">{formatDate(term.end_date)}</span>
                            </td>
                            <td>{term.active ? 'Yes' : 'No'}</td>
                            {(canUpdate || canDelete) && (
                                <td>
                                    {canUpdate && <button onClick={() => openEditModal(term)} className='submit-button text-button margin-right-button'>Update</button>}
                                    {canUpdate && <button onClick={() => handleArchive(term.id)} className="archive-button cancel-button text-button margin-right-button ">
                                        {term.active ? 'Archive' : 'Unarchive'}
                                    </button>}
                                    {canDelete && <button onClick={() => handleDelete(term.id)} className='submit-button text-button'>Delete</button>}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default Terms;
